import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { IApiResponse } from "../../../../utils/types/api-response";
import {
    sendStatementOfAccount,
    createConsent,
    updateConsent,
    updateCollateralConsentStatus,
    uploadConsentDocuments,
    renewConsent,
    createProviderAccommodation,
    deleteProviderAccommodation,
    updateProviderAccommodation,
} from "../../services/reports.services";
import {
    IStatementOfAccountFilters,
    IStatementOfAccountPreviewResponse,
    ISendStatementOfAccount,
    ICreateConsentRequest,
    IUpdateConsentRequest,
    IRenewConsentRequest,
    IConsentReport,
    ICollateralConsentReport,
    IUpdateCollateralConsentStatusRequest,
    ICreateProviderAccommodationPayload,
    IProviderAccommodation,
    IProviderAccommodationPayload,
} from "../../types/reports.types";

// Hook to send clients statement of account
export function useSendStatementOfAccount(filters: IStatementOfAccountFilters) {
    return useMutation<
        IApiResponse<IStatementOfAccountPreviewResponse>,
        AxiosError<IApiResponse<IStatementOfAccountPreviewResponse>>,
        ISendStatementOfAccount
    >({
        mutationKey: ["send-statement"],
        mutationFn: (payload: ISendStatementOfAccount) =>
            sendStatementOfAccount(payload, filters),
    });
}

// Consent Reports Mutation Hooks
export function useCreateConsent() {
    return useMutation<
        IApiResponse<IConsentReport>,
        AxiosError<IApiResponse<string>>,
        ICreateConsentRequest
    >({
        mutationFn: createConsent,
        mutationKey: ["create-consent"],
    });
}

export function useUpdateConsent() {
    const queryClient = useQueryClient();

    return useMutation<
        IApiResponse<IConsentReport>,
        AxiosError<IApiResponse<string>>,
        { consentId: string; data: IUpdateConsentRequest }
    >({
        mutationKey: ["update-consent"],
        mutationFn: ({ consentId, data }) => updateConsent(consentId, data),
        onSuccess: () => {
            // Invalidate consent reports queries to refetch data
            queryClient.invalidateQueries({ queryKey: ["consent-reports"] });
        },
    });
}

export function useUpdateCollateralConsentStatus() {
    const queryClient = useQueryClient();

    return useMutation<
        IApiResponse<ICollateralConsentReport>,
        AxiosError<IApiResponse<string>>,
        {
            collateralConsentId: string;
            data: IUpdateCollateralConsentStatusRequest;
        }
    >({
        mutationFn: ({ collateralConsentId, data }) =>
            updateCollateralConsentStatus(collateralConsentId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["collateral-consent-reports"],
            });
        },
    });
}

export function useUploadConsentDocuments() {
    return useMutation<
        IApiResponse<{ uploaded_documents: string[] }>,
        AxiosError<IApiResponse<string>>,
        { consentId: string; formData: FormData }
    >({
        mutationFn: ({ consentId, formData }) =>
            uploadConsentDocuments(consentId, formData),
        mutationKey: ["upload-consent-documents"],
    });
}

export function useRenewConsent() {
    return useMutation<
        IApiResponse<IConsentReport>,
        AxiosError<IApiResponse<string>>,
        { consentId: string; data: IRenewConsentRequest }
    >({
        mutationFn: ({ consentId, data }) => renewConsent(consentId, data),
        mutationKey: ["renew-consent"],
    });
}

export function useCreateProviderAccommodation() {
    return useMutation<
        IApiResponse<IProviderAccommodation>,
        AxiosError<IApiResponse<IProviderAccommodation>>,
        ICreateProviderAccommodationPayload
    >({
        mutationKey: ["create-provider-accommodation"],
        mutationFn: (payload) => createProviderAccommodation(payload),
    });
}

export function useUpdateProviderAccommodation(accommodationId: string) {
    return useMutation<
        IApiResponse<IProviderAccommodation>,
        AxiosError<IApiResponse<IProviderAccommodation>>,
        IProviderAccommodationPayload
    >({
        mutationKey: ["update-provider-accommodation", accommodationId],
        mutationFn: (payload) =>
            updateProviderAccommodation(accommodationId, payload),
    });
}

export function useDeleteProviderAccommodation(accommodationId: string) {
    return useMutation<
        IApiResponse<{ accommodation_id: string }>,
        AxiosError<IApiResponse<{ accommodation_id: string }>>,
        string
    >({
        mutationKey: ["delete-provider-accommodation", accommodationId],
        mutationFn: (reason) =>
            deleteProviderAccommodation(accommodationId, reason),
    });
}
